import * as React from "react";
import { Box, Stack, Typography } from "@mui/material";

import type { CampaignRequestDetail, DocumentKind } from "@/hooks/api/use-campaign-requests";

import { DocumentList } from "../components";
import { DOCUMENT_KIND_LABELS, isFrozen } from "../constants";
import { labelOf } from "../humanise";
import { Panel } from "./parts";

/**
 * What has been sent, and the slots for what has not.
 *
 * UX §12 note 3: the missing purchase order stays visible as a soft slot rather than disappearing
 * once the wizard is past the step that asked for it. The slot carries the name of the thing and
 * nothing else — why it is wanted is on the Summary tab, under the one heading that answers "what
 * do you still need from me", and saying it in both places would be saying it twice.
 *
 * Uploading stays open on a confirmed request. Attaching the PO afterwards is the whole point of
 * the slot; only deleting closes once the request is frozen.
 */

export interface DocumentsTabProps {
	request: CampaignRequestDetail;
}

/** The paperwork this request is still short of, in the order it is usually chased. */
function missingKinds(request: CampaignRequestDetail): DocumentKind[] {
	const attached = new Set(request.documents.map((document) => document.kind));
	const kinds: DocumentKind[] = [];

	if (request.blockers.includes("leads_missing") && !attached.has("lead_list")) kinds.push("lead_list");
	if (request.blockers.includes("flow_missing") && !attached.has("call_flow")) kinds.push("call_flow");
	if (request.blockers.includes("sow_missing")) kinds.push("sow");
	if (request.blockers.includes("po_missing")) kinds.push("po");

	return kinds;
}

export function DocumentsTab({ request }: DocumentsTabProps): React.JSX.Element {
	const missing = missingKinds(request);

	return (
		<Stack spacing={2}>
			{/* Titled for what it is rather than for the tab it sits on: this is the whole record,
			    as against the two kinds the Configuration tab shows beside the agents they belong to. */}
			<DocumentList
				requestId={request.id}
				documents={request.documents}
				title="Everything attached"
				emptyText="Nothing attached yet."
				allowDelete={!isFrozen(request.state)}
			/>

			{missing.length > 0 ? (
				<Panel title="Still to send">
					<Stack spacing={1}>
						{missing.map((kind) => (
							<Box
								key={kind}
								sx={{
									border: "1px dashed var(--mui-palette-warning-main)",
									borderRadius: 1,
									px: 1.5,
									py: 1.25,
								}}
							>
								<Typography variant="body2">{labelOf(DOCUMENT_KIND_LABELS, kind)}</Typography>
							</Box>
						))}
					</Stack>
				</Panel>
			) : null}
		</Stack>
	);
}
